/*
 * Barra product page
 *   <barra-gallery-count>  "3 / 11" photo counter that follows the gallery carousel (and the phone progress line)
 *   <barra-ship-note>      on weekends and holidays: "Orders placed today start processing Monday"
 *   <barra-buy-bar>        bottom Add to cart bar, shown once the main button has scrolled out of view
 */

// Everything below stays private to this file, so its names can't collide with an app's scripts
(() => {

/* ---------------------------------------------------------------------------------------------------------------------
   PHOTO COUNTER
   ------------------------------------------------------------------------------------------------------------------ */

if (!window.customElements.get('barra-gallery-count')) {
  window.customElements.define('barra-gallery-count', class extends HTMLElement {
    connectedCallback() {
      const carousel = document.getElementById(this.getAttribute('aria-controls'));

      if (!carousel) {
        return;
      }

      this._abortController = new AbortController();

      // Counts only the photos shown for the selected variant
      const update = () => requestAnimationFrame(() => {
        const items = carousel.visibleItems || [], index = items.indexOf(carousel.selectedSlide);

        if (items.length) {
          this.textContent = `${Math.max(index, 0) + 1} / ${items.length}`;
          // Drives the progress line under the photo on phones
          carousel.closest('.product-gallery')?.style.setProperty('--barra-gallery-progress', ((Math.max(index, 0) + 1) / items.length).toFixed(3));
        }
      });

      ['carousel:select', 'carousel:change', 'carousel:filter'].forEach((name) => carousel.addEventListener(name, update, { signal: this._abortController.signal }));
      update();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   SHIPPING NOTE
   Orders ship within 48 hours, Monday to Friday, never on weekends or holidays. No exact date is promised; on a weekend
   or holiday the note says which business day processing starts. Worked out on the shopper's device (pages are
   cached), using the Eastern time calendar day.
   ------------------------------------------------------------------------------------------------------------------ */

const SHIPPING_TIME_ZONE = 'America/New_York';

// Observed holidays: a Saturday holiday is observed the Friday before, a Sunday holiday the Monday after
const observed = (date) => {
  const day = date.getUTCDay();
  return day === 6 ? addDays(date, -1) : day === 0 ? addDays(date, 1) : date;
};

const addDays = (date, days) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));

// The nth given weekday of a month (n = -1 for the last one)
const nthWeekday = (year, month, weekday, n) => {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month, 1));
    return addDays(first, (weekday - first.getUTCDay() + 7) % 7 + (n - 1) * 7);
  }

  const last = new Date(Date.UTC(year, month + 1, 0));
  return addDays(last, -((last.getUTCDay() - weekday + 7) % 7));
};

// Warehouse closed days (confirmed by Barra): the eves fall on the calendar date, the federal holidays use the observed day
const holidaysFor = (year) => {
  const thanksgiving = nthWeekday(year, 10, 4, 4);

  return [
    new Date(Date.UTC(year, 0, 1)), // New Year's Day
    observed(new Date(Date.UTC(year, 0, 1))),
    nthWeekday(year, 4, 1, -1), // Memorial Day
    observed(new Date(Date.UTC(year, 6, 4))), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor Day
    addDays(thanksgiving, -1), // Thanksgiving Eve
    thanksgiving, // Thanksgiving
    addDays(thanksgiving, 1), // Day after Thanksgiving
    new Date(Date.UTC(year, 11, 24)), // Christmas Eve
    new Date(Date.UTC(year, 11, 25)), // Christmas Day
    observed(new Date(Date.UTC(year, 11, 25))),
    new Date(Date.UTC(year, 11, 31)) // New Year's Eve
  ].map((date) => date.getTime());
};

const isBusinessDay = (date) => {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6 && !holidaysFor(date.getUTCFullYear()).includes(date.getTime());
};

const nextBusinessDay = (date) => {
  let next = addDays(date, 1);

  while (!isBusinessDay(next)) {
    next = addDays(next, 1);
  }

  return next;
};

const todayInShippingZone = () => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: SHIPPING_TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date()).map((part) => [part.type, part.value]));
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
};

if (!window.customElements.get('barra-ship-note')) {
  window.customElements.define('barra-ship-note', class extends HTMLElement {
    connectedCallback() {
      try {
        const today = todayInShippingZone();

        if (!isBusinessDay(today)) {
          const day = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long' }).format(nextBusinessDay(today));
          this.textContent = `Orders placed today start processing ${day}.`;
        }
      } catch (error) {
        // The general note stays in place
      }
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   BUY BAR
   ------------------------------------------------------------------------------------------------------------------ */

if (!window.customElements.get('barra-buy-bar')) {
  window.customElements.define('barra-buy-bar', class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._ticking = false;

      // The product info can be swapped for a fresh copy (variant changes, apps), so the button is looked up on every
      // check rather than kept, and checks are throttled to one per frame
      const schedule = () => {
        if (!this._ticking) {
          this._ticking = true;
          requestAnimationFrame(() => {
            this._ticking = false;
            this._toggle();
          });
        }
      };

      window.addEventListener('scroll', schedule, { passive: true, signal: this._abortController.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this._abortController.signal });
      schedule();
    }

    disconnectedCallback() {
      this._abortController.abort();
      document.documentElement.classList.remove('barra-buy-bar-open');
    }

    _toggle() {
      const form = document.getElementById(this.getAttribute('form')),
            button = form?.querySelector('buy-buttons') || form,
            footer = document.querySelector('.shopify-section-group-footer-group');

      // Shown once the main button is above the screen (not before the shopper reaches it), hidden over the footer
      const buttonPassed = !!button && button.getBoundingClientRect().bottom < 0,
            footerVisible = !!footer && footer.getBoundingClientRect().top < window.innerHeight,
            visible = buttonPassed && !footerVisible;

      if (visible !== this.classList.contains('is-visible')) {
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
        document.documentElement.classList.toggle('barra-buy-bar-open', visible);
      }
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   COMPLETE YOUR SETUP
   The product shown plus its complementary products, each with a tick box; one button adds every ticked item. The
   cart confirmation then opens as for a normal Add to cart, with a note for the extra items.
   ------------------------------------------------------------------------------------------------------------------ */

if (!window.customElements.get('barra-setup')) {
  window.customElements.define('barra-setup', class extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._money = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.getAttribute('currency') || 'USD' });
      this.addEventListener('change', (event) => this._onChange(event), { signal });
      this.querySelector('[data-add]').addEventListener('click', () => this._addToCart(), { signal });

      // Anything already in the cart starts unticked (so the setup never adds a second rifle by surprise)
      this._syncWithCart();
      document.addEventListener('cart:change', (event) => this._syncWithCart(event.detail?.cart), { signal });

      // The main product follows the variant and quantity picked in the buy box
      const form = document.forms[this.getAttribute('form')];
      form?.addEventListener('variant:change', (event) => this._onVariantChange(event), { signal });
      form?.addEventListener('change', (event) => event.target.name === 'quantity' && this._update(), { signal });

      // Complementary products arrive after the page has loaded; Shopify removes the list when there are none, and then
      // the researched add-ons (if any) are used. A request that never answers falls back after a few seconds.
      // Only list changes count (the total and count this element writes itself must not re-trigger it)
      this._observer = new MutationObserver((records) => {
        if (records.some((record) => !record.target.closest?.('.barra-setup__footer'))) {
          this._update();
        }
      });
      this._observer.observe(this, { childList: true, subtree: true });
      this._fallbackTimer = setTimeout(() => this._useFallback(), 6000);
      this._update();
    }

    disconnectedCallback() {
      this._abortController.abort();
      this._observer?.disconnect();
      clearTimeout(this._fallbackTimer);
    }

    // On bundles: whether an add-on is already in the bundle (or a second scope / set of rings it doesn't need)
    _isExcluded(item) {
      const ids = (this.dataset.excludeIds || '').split(','), kinds = (this.dataset.excludeKinds || '').split(',');
      return ids.includes(item.dataset.productId) || (item.dataset.kind && kinds.includes(item.dataset.kind));
    }

    // Two sizes of the same thing (CO2 15- and 40-count, say) count as one: the family is the title without its pack size
    _family(item) {
      return (item.querySelector('.barra-setup__name')?.textContent || '').toLowerCase().split(' - ')[0].replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim();
    }

    // Appends researched add-ons that aren't listed yet until five are shown. Runs once Shopify's list has arrived
    // (or was removed because it was empty, or didn't answer in time).
    _useFallback() {
      const template = this.querySelector('template[data-barra-fallback]');

      if (!template) {
        return;
      }

      const target = 5, shown = new Set([...this.querySelectorAll('.barra-setup__item--rec')].filter((item) => !this._isExcluded(item)).map((item) => item.dataset.productId));
      let list = this.querySelector('product-recommendations .barra-setup__list, .barra-setup__list--fallback');

      if (!list) {
        list = document.createElement('ul');
        list.className = 'barra-setup__list barra-setup__list--fallback';
        list.setAttribute('role', 'list');
        template.before(list);
      }

      const families = new Set([...this.querySelectorAll('.barra-setup__item--rec')].map((item) => this._family(item)));
      let airguns = this.querySelectorAll('.barra-setup__item--rec[data-airgun]').length;
      template.content.querySelectorAll('.barra-setup__item--rec').forEach((item) => {
        if (shown.size >= target || shown.has(item.dataset.productId) || this._isExcluded(item) || families.has(this._family(item)) || (item.hasAttribute('data-airgun') && airguns >= 1)) {
          return;
        }

        if (item.hasAttribute('data-airgun')) airguns++;

        shown.add(item.dataset.productId);
        families.add(this._family(item));
        list.appendChild(item.cloneNode(true));
      });

      template.remove();
    }

    get _mainQuantity() {
      return Math.max(1, Number(document.forms[this.getAttribute('form')]?.querySelector('[name="quantity"]')?.value) || 1);
    }

    get _checks() {
      return Array.from(this.querySelectorAll('.barra-setup__check'));
    }

    async _syncWithCart(cart) {
      try {
        cart = cart?.items ? cart : await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      } catch (error) {
        return;
      }

      const inCart = new Set(cart.items.map((item) => String(item.variant_id)));

      this._checks.forEach((check) => {
        const row = check.closest('.barra-setup__item'), already = inCart.has(String(check.dataset.variantId));

        if (already && !check.dataset.barraInCart) {
          check.checked = false;
        }

        check.dataset.barraInCart = already ? 'true' : '';
        row?.classList.toggle('is-in-cart', already);
      });

      this._update();
    }

    _onChange(event) {
      const select = event.target.closest('.barra-setup__select');

      if (select) {
        const option = select.selectedOptions[0], row = select.closest('.barra-setup__row'), check = row.querySelector('.barra-setup__check');
        check.dataset.variantId = option.value;
        check.dataset.price = option.dataset.price;
        row.querySelector('[data-price-label]').textContent = this._format(option.dataset.price);
      }

      this._update();
    }

    _onVariantChange(event) {
      const variant = event.detail?.variant, check = this.querySelector('.barra-setup__item--main .barra-setup__check');

      if (!check) {
        return;
      }

      // A combination of options that doesn't exist: the buy box says Unavailable, so the setup can't add it either
      if (!variant) {
        check.checked = false;
        check.disabled = true;
        this._update();
        return;
      }

      // Coming back from a sold-out or unavailable choice ticks the product again (unless it's already in the cart)
      const wasOff = check.disabled;
      check.dataset.variantId = variant.id;
      check.dataset.price = variant.price;
      check.dataset.barraInCart = '';
      check.disabled = !variant.available;
      check.checked = variant.available && (check.checked || wasOff);
      check.closest('.barra-setup__row').querySelector('[data-price-label]').textContent = this._format(variant.price);
      this._update();
      this._syncWithCart();
    }

    _format(cents) {
      return this._money.format(Number(cents) / 100);
    }

    // Every add-on starts ticked; the shopper unticks what they don't want. Runs once, when the add-on list is complete
    // (items already in the cart are unticked again by _syncWithCart).
    _presetTicks() {
      if (this._preset || this.querySelector('template[data-barra-fallback]')) {
        return;
      }

      this._preset = true;
      this.querySelectorAll('.barra-setup__item--rec .barra-setup__check').forEach((check) => {
        check.checked = !check.disabled;
      });
      this._syncWithCart();
    }

    _update() {
      // Bundles: drop suggestions the bundle already covers (Shopify's list arrives after load)
      // A setup suggests one airgun at most: a list of rifles under a scope or a tin of pellets looks wrong
      const seenFamilies = new Set();
      let airgunShown = false;
      this.querySelectorAll('.barra-setup__item--rec').forEach((item) => {
        if (item.closest('template')) return;
        const family = this._family(item), airgun = item.hasAttribute('data-airgun');
        if (this._isExcluded(item) || seenFamilies.has(family) || (airgun && airgunShown)) item.remove();
        else { seenFamilies.add(family); airgunShown ||= airgun; }
      });

      // Five add-ons at most: the complementary list is ordered best-first, and the rest only stand in for sold-out items
      if (!this.querySelector('template[data-barra-fallback]')) {
        [...this.querySelectorAll('.barra-setup__item--rec')].filter((item) => !item.closest('template')).slice(5).forEach((item) => item.remove());
      }

      // A product that must be offered (the compressor on PCP bundles) joins the list once it has arrived
      const mustShow = this.querySelector('template[data-barra-must-show]'), recs = this.querySelector('product-recommendations');
      if (mustShow && (!recs || recs.querySelector('.barra-setup__list') || !this.querySelector('template[data-barra-fallback]'))) {
        const item = mustShow.content.querySelector('.barra-setup__item--rec');
        let list = this.querySelector('product-recommendations .barra-setup__list, .barra-setup__list--fallback');
        if (!list && !this.querySelector('template[data-barra-fallback]')) {
          list = document.createElement('ul'); list.className = 'barra-setup__list barra-setup__list--fallback'; list.setAttribute('role', 'list');
          mustShow.before(list);
        }
        if (list) {
          if (item && !this.querySelector(`.barra-setup__item--rec[data-product-id="${item.dataset.productId}"]`)) list.prepend(item.cloneNode(true));
          mustShow.remove();
        }
      }

      // Shopify's list has arrived (or the element was removed because there were none): top it up
      const recommendations = this.querySelector('product-recommendations');
      if (this.querySelector('template[data-barra-fallback]') && (!recommendations || recommendations.querySelector('.barra-setup__list'))) {
        this._useFallback();
      }

      if (this.querySelector('.barra-setup__item--rec')) {
        this._presetTicks();
      }

      // Rows that arrive after the ticks were set (a slow recommendations answer) still get checked against the cart
      const rowCount = this.querySelectorAll('.barra-setup__item--rec').length;
      if (this._preset && rowCount > (this._rowCount ?? rowCount)) {
        this._syncWithCart();
      }
      this._rowCount = rowCount;

      const hasRecommendations = !!this.querySelector('.barra-setup__item--rec'),
            checked = this._checks.filter((check) => check.checked && !check.disabled),
            mainCheck = this.querySelector('.barra-setup__item--main .barra-setup__check'),
            total = checked.reduce((sum, check) => sum + Number(check.dataset.price) * (check === mainCheck ? this._mainQuantity : 1), 0);

      const totalLabel = this.querySelector('[data-total]'), countLabel = this.querySelector('[data-count]'), formatted = this._format(total);

      this.classList.toggle('is-ready', hasRecommendations);
      if (totalLabel.textContent !== formatted) totalLabel.textContent = formatted;
      if (countLabel.textContent !== String(checked.length)) countLabel.textContent = checked.length;
      this.querySelector('[data-add]').disabled = checked.length === 0;
      this.querySelectorAll('.barra-setup__item').forEach((item) => item.classList.toggle('is-off', !item.querySelector('.barra-setup__check')?.checked));
    }

    async _addToCart() {
      const button = this.querySelector('[data-add]'), error = this.querySelector('.barra-setup__error'),
            form = document.forms[this.getAttribute('form')],
            mainCheck = this.querySelector('.barra-setup__item--main .barra-setup__check'),
            checked = this._checks.filter((check) => check.checked && !check.disabled);

      // Required product options (engraving text and the like) are filled in through the main form
      if (mainCheck?.checked && form && !form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (!checked.length) {
        return;
      }

      const mainQuantity = this._mainQuantity,
            items = checked.map((check) => ({ id: Number(check.dataset.variantId), quantity: check === mainCheck ? mainQuantity : 1 }));

      // Main form properties (if any) travel with the product shown
      if (mainCheck?.checked && form) {
        const properties = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        if (Object.keys(properties).length) items[checked.indexOf(mainCheck)].properties = properties;
      }

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      error.hidden = true;

      const add = async (list) => {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: list, sections: 'variant-added', sections_url: `${Shopify.routes.root}variants/${list[0].id}` })
        });
        let json = {};
        try { json = await response.json(); } catch (error) {}
        return { ok: response.ok, json };
      };

      try {
        let { ok, json } = await add(items);
        const failed = [];

        // Shopify turns down the whole request when one item can't be added (sold out since the page loaded, say), so
        // add them one at a time instead, the product shown first, and untick whatever still can't be added
        if (!ok && items.length > 1) {
          const added = [], sections = {};
          for (const [index, item] of items.entries()) {
            const result = await add([item]);
            if (result.ok) {
              added.push(...(result.json.items || []));
              Object.assign(sections, result.json.sections);
            } else {
              const check = checked[index];
              check.checked = false;
              failed.push(check.closest('.barra-setup__item')?.querySelector('.barra-setup__name')?.textContent.trim() || 'One item');
            }
          }
          if (added.length) {
            ok = true;
            json = { items: added, sections };
            items.splice(0, items.length, ...added);
          }
        }

        if (!ok) {
          throw new Error(json.description || json.message || 'This could not be added to your cart. Please try again.');
        }

        if (failed.length) {
          error.textContent = `${failed.join(', ')} couldn't be added (${failed.length > 1 ? 'they may have' : 'it may have'} just sold out). Everything else is in your cart.`;
          error.hidden = false;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = json.sections;

        this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));

        // The confirmation shows the first item; name how many others came with it
        if (items.length > 1) {
          requestAnimationFrame(() => {
            const details = document.querySelector('.barra-added__details');
            if (details && !details.querySelector('.barra-added__extra')) {
              const extra = document.createElement('p');
              extra.className = 'barra-added__meta barra-added__extra';
              extra.textContent = `+ ${items.length - 1} more item${items.length > 2 ? 's' : ''} from your setup`;
              details.appendChild(extra);
            }
          });
        }
      } catch (exception) {
        error.textContent = exception.message;
        error.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        button.removeAttribute('aria-busy');
        this._update();
      }
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   DESCRIPTION: READ MORE (phones)
   Long descriptions show their opening lines with a "Read more" toggle; the full text stays in the page for search
   engines and screen readers.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const setup = () => {
    document.querySelectorAll('.shopify-section--main-product .product-info__description:not([data-barra-clamp])').forEach((description) => {
      description.setAttribute('data-barra-clamp', '');

      if (!window.matchMedia('(max-width: 699px)').matches || description.scrollHeight < 220) {
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'barra-read-more';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'Read more';
      description.classList.add('is-clamped');
      description.after(button);

      button.addEventListener('click', () => {
        const expanded = description.classList.toggle('is-clamped') === false;
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', setup) : setup();
})();

/* ---------------------------------------------------------------------------------------------------------------------
   ONE PANEL AT A TIME
   In each group of collapsible panels on the product page (Warranty / Shipping / Manual / Warnings, the Operation /
   Materials tabs on phones, the FAQ), opening one closes the one that was open, with the theme's own animation.
   ------------------------------------------------------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const summary = event.target.closest('summary');
  const details = summary?.closest('details[is="accordion-disclosure"]');
  const group = details?.closest('.product-info, .shopify-section--tabs, .shopify-section--faq');

  if (!details || !group || !group.closest('.template-product, body')) {
    return;
  }

  // The theme opens the panel on this same click; close the others once it has
  requestAnimationFrame(() => {
    if (!details.open && details.getAttribute('aria-expanded') !== 'true') {
      return;
    }

    group.querySelectorAll('details[is="accordion-disclosure"][open]').forEach((other) => {
      if (other !== details) {
        other.open = false;
      }
    });
  });
});

/* ---------------------------------------------------------------------------------------------------------------------
   LIFESTYLE BAND
   Puts one of the product's own lifestyle photos behind the dark details band. Gallery photos after the first are
   checked in order (only once the band is near the screen); white-background studio shots are skipped by sampling the
   image, and the first real lifestyle photo is used. Nothing is shown if none is found.
   ------------------------------------------------------------------------------------------------------------------ */

if (!window.customElements.get('barra-band-image')) {
  window.customElements.define('barra-band-image', class extends HTMLElement {
    connectedCallback() {
      this._band = this.closest('.section');
      const candidates = (this.dataset.candidates || '').split('|').filter(Boolean);

      if (!this._band || !candidates.length || !('IntersectionObserver' in window)) {
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          this._pick(candidates);
        }
      }, { rootMargin: '600px 0px' });

      observer.observe(this._band);
    }

    async _pick(candidates) {
      for (const url of candidates) {
        const image = await this._load(url.replace(/width=\d+/, 'width=80'));

        if (image && this._isLifestyle(image)) {
          // Sized to the screen: about 800px wide on phones, up to 1600px on large desktops
          const width = Math.min(1600, Math.ceil(window.innerWidth * Math.min(window.devicePixelRatio || 1, 2) / 400) * 400);
          const full = new Image();
          full.src = (url.startsWith('//') ? `https:${url}` : url).replace(/width=\d+/, `width=${width}`);
          await full.decode().catch(() => {});
          this._band.style.setProperty('--barra-band-image', `url("${full.src}")`);
          this._band.classList.add('has-band-image');
          return;
        }
      }
    }

    _load(url) {
      return new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => resolve(null);
        image.src = url.startsWith('//') ? `https:${url}` : url;
      });
    }

    // A studio shot is white all the way round its edges; a lifestyle photo isn't (a bright sky alone doesn't count)
    _isLifestyle(image) {
      try {
        const canvas = document.createElement('canvas'), size = 40;
        canvas.width = canvas.height = size;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, size, size);
        const data = context.getImageData(0, 0, size, size).data;
        let edge = 0, white = 0;

        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            if (x > 1 && y > 1 && x < size - 2 && y < size - 2) continue;
            const i = (y * size + x) * 4;
            edge++;
            if (data[i] > 228 && data[i + 1] > 228 && data[i + 2] > 228) white++;
          }
        }

        return white / edge < .45;
      } catch (error) {
        return false;
      }
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   REVIEWS ON PHONES
   Long review lists are shown partly, with a "Show all reviews" button, so the specs, FAQ and related products below
   stay within reach. Only applies when the widget is taller than the collapsed height.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const setup = () => {
    const block = [...document.querySelectorAll('.template-product .shopify-app-block')].find((element) => element.querySelector('[data-bv-show="reviews"]'));

    if (!block || block.dataset.barraCollapse || !window.matchMedia('(max-width: 699px)').matches) {
      return;
    }

    // The widget fills in after load; wait until it is clearly long
    const check = () => {
      if (block.dataset.barraCollapse || block.scrollHeight < 2000) {
        return;
      }

      block.dataset.barraCollapse = 'true';
      block.classList.add('is-collapsed');

      const wrapper = document.createElement('div'), button = document.createElement('button');
      wrapper.className = 'barra-reviews-more';
      button.type = 'button';
      button.className = 'button';
      button.textContent = 'Show all reviews';
      button.setAttribute('aria-expanded', 'false');
      wrapper.appendChild(button);
      block.after(wrapper);

      button.addEventListener('click', () => {
        const collapsed = block.classList.toggle('is-collapsed');
        button.setAttribute('aria-expanded', String(!collapsed));
        button.textContent = collapsed ? 'Show all reviews' : 'Show fewer reviews';

        if (collapsed) {
          block.scrollIntoView({ block: 'start' });
        }
      });
    };

    new ResizeObserver(check).observe(block);
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', setup) : setup();
})();

/* ---------------------------------------------------------------------------------------------------------------------
   BREADCRUMBS: ONE LINE
   A trail too long for the screen drops its "Shop" step (the two category levels stay), checked again on resize.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const trails = [...document.querySelectorAll('.barra-crumbs')].filter((trail) => trail.querySelectorAll('.barra-crumbs__step').length >= 2);

  if (!trails.length) {
    return;
  }

  const fit = () => trails.forEach((trail) => {
    trail.classList.remove('is-compact');
    if (trail.scrollWidth > trail.clientWidth + 1) {
      trail.classList.add('is-compact');
    }
  });

  fit();
  window.addEventListener('resize', fit, { passive: true });
  document.fonts?.ready.then(fit);
})();

/* ---------------------------------------------------------------------------------------------------------------------
   BREADCRUMBS
   A product opened from a filtered or sorted collection: the trail's link to that collection returns to the same view
   (filters, sort and page kept) instead of the unfiltered collection.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  let from;
  try {
    from = new URL(document.referrer);
  } catch (error) {
    return;
  }

  if (from.origin !== location.origin || !from.pathname.startsWith('/collections/') || !from.search) {
    return;
  }

  document.querySelectorAll('.barra-crumbs a[href]').forEach((link) => {
    if (new URL(link.href, location.origin).pathname === from.pathname) {
      link.href = from.pathname + from.search;
    }
  });
})();

/* ---------------------------------------------------------------------------------------------------------------------
   COLLECTION FILTER BUTTON
   The floating "Filter and sort" button on phones only shows while the product grid is on screen, so it never covers
   the buying guide, the footer or anything else below the products.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const button = document.querySelector('.facets__floating-filter');
  const grid = button?.closest('.shopify-section')?.querySelector('product-list, .product-list, .collection__results, .collection') || button?.closest('.shopify-section');

  if (!button || !grid || !('IntersectionObserver' in window)) {
    return;
  }

  // The bottom 140px of the screen is where the button floats: it hides once the grid has scrolled out of that band
  new IntersectionObserver((entries) => button.classList.toggle('is-hidden', !entries[0].isIntersecting), { rootMargin: '0px 0px -140px 0px' }).observe(grid);
})();

/* ---------------------------------------------------------------------------------------------------------------------
   COMPACT RATING
   Bazaarvoice's summary draws five stars plus its own text; next to the title that crowds the header (long titles,
   sale badges). Its score and review count are read once it has loaded and shown as one compact line ("★ 4.6 (208)")
   that jumps to the reviews. If the numbers can't be read, the original widget stays in place.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const summary = document.querySelector('.shopify-section--main-product .product-info [data-bv-show="rating_summary"]');

  if (!summary) {
    return;
  }

  const build = () => {
    const root = [...summary.querySelectorAll('*')].find((element) => element.shadowRoot)?.shadowRoot || summary.shadowRoot || summary;
    const text = [...root.querySelectorAll('*')].filter((element) => element.tagName !== 'STYLE').map((element) => [...element.childNodes].filter((node) => node.nodeType === 3).map((node) => node.textContent).join(' ')).join(' ').replace(/\s+/g, ' ');
    const match = text.match(/([0-9.]+) out of 5 stars.*?(?:Read )?([0-9,]+) Reviews?/i);

    // No reviews yet: hide the empty stars rather than show "(0)"
    if (!match && /No rating value/i.test(text) && !summary.dataset.barraCompact) {
      summary.dataset.barraCompact = 'empty';
      summary.closest('.product-info__block-item')?.classList.add('barra-rating-empty');
      return true;
    }

    if (!match || summary.dataset.barraCompact) {
      return !!match;
    }

    summary.dataset.barraCompact = 'true';
    const count = match[2].replace(/,/g, ''), link = document.createElement('a');
    link.className = 'barra-rating';
    link.href = '#reviews';
    link.setAttribute('aria-label', `Rated ${match[1]} out of 5 from ${count} reviews. Go to reviews`);
    link.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.2 6.8.8-5 4.7 1.3 6.7L12 17.6 6 20.9l1.3-6.7-5-4.7 6.8-.8L12 2.5Z" fill="currentColor"/></svg><span class="barra-rating__score">${match[1]}</span><span class="barra-rating__count">(${Number(count).toLocaleString('en-US')})</span>`;

    link.addEventListener('click', (event) => {
      const reviews = document.querySelector('[data-bv-show="reviews"]');
      if (reviews) {
        event.preventDefault();
        reviews.closest('.shopify-app-block, .shopify-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    summary.after(link);
    summary.classList.add('barra-rating-source');
    return true;
  };

  // The widget fills in after the page loads
  // (the widget stays invisible meanwhile, so its full-size stars never flash before the compact line; if the numbers
  // can't be read within 10 seconds it is shown as it is)
  let tries = 0;
  const timer = setInterval(() => {
    if (build()) {
      clearInterval(timer);
    } else if (++tries > 40) {
      clearInterval(timer);
      summary.classList.add('barra-rating-fallback');
    }
  }, 250);
})();

/* ---------------------------------------------------------------------------------------------------------------------
   CARD RATINGS
   Bazaarvoice inline ratings on product cards become the same compact "★ 4.6 (208)" as the product page. Cards
   without reviews show nothing.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  const compact = (holder) => {
    if (holder.dataset.barraDone) return;
    const root = [...holder.querySelectorAll('*')].find((element) => element.shadowRoot)?.shadowRoot || holder.shadowRoot || holder;
    const text = [...root.querySelectorAll('*')].filter((element) => element.tagName !== 'STYLE').map((element) => [...element.childNodes].filter((node) => node.nodeType === 3).map((node) => node.textContent).join(' ')).join(' ').replace(/\s+/g, ' ');
    const labels = [...root.querySelectorAll('[aria-label]')].map((element) => element.getAttribute('aria-label')).join(' ');
    const match = `${labels} ${text}`.match(/([0-9.]+) out of 5 stars.*?([0-9,]+) Reviews?/i);

    if (match) {
      holder.dataset.barraDone = 'true';
      const count = Number(match[2].replace(/,/g, ''));
      if (!count) { holder.classList.add('is-empty'); return; }
      const label = document.createElement('span');
      label.className = 'barra-rating barra-rating--card';
      label.setAttribute('aria-label', `Rated ${match[1]} out of 5 from ${count} reviews`);
      label.innerHTML = `<svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.2 6.8.8-5 4.7 1.3 6.7L12 17.6 6 20.9l1.3-6.7-5-4.7 6.8-.8L12 2.5Z" fill="currentColor"/></svg><span class="barra-rating__score">${match[1]}</span><span class="barra-rating__count">(${count.toLocaleString('en-US')})</span>`;
      holder.after(label);
      holder.classList.add('is-replaced');
    } else if (/No rating value|0 Reviews?/i.test(text)) {
      holder.dataset.barraDone = 'true';
      holder.classList.add('is-empty');
    }
  };

  const scan = () => document.querySelectorAll('.product-card__rating[data-bv-show="inline_rating"]:not([data-barra-done])').forEach(compact);
  setInterval(scan, 600);
})();

/* ---------------------------------------------------------------------------------------------------------------------
   LIVE REVIEW FIGURES (model landing pages)
   Reads the model's Bazaarvoice review summary (the same request Bazaarvoice's own rating widget makes) and fills in
   the average, the spread from five stars to one, the five-star share and the "would recommend" share. The section
   stays hidden until then and is removed if the figures can't be read.
   ------------------------------------------------------------------------------------------------------------------ */

if (!window.customElements.get('barra-review-stats')) {
  window.customElements.define('barra-review-stats', class extends HTMLElement {
    async connectedCallback() {
      const id = this.getAttribute('product-id'),
            url = `https://apps.bazaarvoice.com/bfd/v1/clients/barraairguns/api-products/cv2/resources/data/display/0.2alpha/product/summary?productid=${encodeURIComponent(id)}&contentType=reviews&reviewDistribution=primaryRating%2Crecommended&rev=0&contentlocale=en*%2Cen_US`;

      try {
        const response = await fetch(url, { headers: { 'bv-bfd-token': '19380,main_site,en_US' } });
        const summary = (await response.json())?.response?.reviewSummary;
        const total = summary?.numReviews || 0;

        if (!response.ok || !total) {
          throw new Error('No review figures');
        }

        this._render(summary, total);
        this.hidden = false;
      } catch (error) {
        this.closest('.shopify-section')?.remove();
      }
    }

    _render(summary, total) {
      const set = (selector, text) => { const element = this.querySelector(selector); if (element) element.textContent = text; },
            average = summary.primaryRating.average,
            counts = Object.fromEntries((summary.primaryRating.distribution || []).map((entry) => [entry.key, entry.count])),
            five = counts[5] || 0,
            recommended = summary.recommended?.distribution || [],
            yes = recommended.find((entry) => entry.key === true)?.count || 0,
            no = recommended.find((entry) => entry.key === false)?.count || 0;

      set('[data-average]', average.toFixed(1));
      set('[data-count]', total.toLocaleString('en-US'));
      set('[data-five]', `${Math.round(five / total * 100)}%`);
      set('[data-five-note]', `${five.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} reviews`);

      // Stars filled to the average, to the nearest tenth
      const stars = this.querySelector('[data-stars]');
      if (stars) stars.style.setProperty('--barra-stars-fill', `${(average / 5 * 100).toFixed(1)}%`);

      // Not every reviewer answers the "would you recommend" question; the share counts those who did
      if (yes + no > 0) {
        set('[data-recommend]', `${Math.round(yes / (yes + no) * 100)}%`);
        set('[data-recommend-note]', `${yes.toLocaleString('en-US')} of ${(yes + no).toLocaleString('en-US')} reviewers who answered`);
      } else {
        this.querySelector('[data-recommend-block]')?.remove();
      }

      const bars = this.querySelector('[data-bars]');
      if (bars) {
        bars.innerHTML = [5, 4, 3, 2, 1].map((stars) => {
          const count = counts[stars] || 0, share = (count / total * 100).toFixed(1);
          return `<li class="barra-reviews__bar"><span>${stars} star</span><span class="barra-reviews__track"><span style="width: ${share}%"></span></span><span>${count.toLocaleString('en-US')}</span></li>`;
        }).join('');
        bars.setAttribute('aria-label', 'Ratings by star');
      }
    }
  });
}

/* ---------------------------------------------------------------------------------------------------------------------
   #reviews on product pages (linked from the landing pages): scroll to the Bazaarvoice reviews once they are on the page
   ------------------------------------------------------------------------------------------------------------------ */

if (location.hash === '#reviews' && document.body.classList.contains('template-product')) {
  let tries = 0;
  const jump = setInterval(() => {
    const reviews = document.querySelector('.shopify-app-block:has([data-bv-show="reviews"])') || document.querySelector('[data-bv-show="reviews"]');
    if (reviews || ++tries > 20) {
      clearInterval(jump);
      reviews?.scrollIntoView({ block: 'start' });
    }
  }, 300);
}

})();
